/**
 * Outil « Thalès glissant » (Boîte à outils, 4e → 2nde).
 *
 * Deux sécantes en A, la parallèle (MN) à (BC) qu'on fait glisser : les trois
 * rapports AM/AB, AN/AC et MN/BC restent égaux, y compris en configuration
 * papillon. Le mode « réciproque » libère N : on voit quand (MN) redevient
 * parallèle, et le piège des points qui ne sont pas dans le même ordre.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, ParamSlider, PRIMARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { makeFrame, snapPoint, type Pt } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import { DragHandle, INK, MUTED, PlotGrid, FIGURE_BG_CLASS } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  GOALS,
  isTriangle,
  K_MAX,
  K_MIN,
  K_STEP,
  pointAt,
  projectParam,
  read,
  snapK,
  type ThalesFigure,
} from './thales';

const SIZE = 320;
const VP = { xMin: -7, xMax: 7, yMin: -7, yMax: 7 };
const frame = makeFrame(VP, SIZE);
const START: ThalesFigure = {
  A: { x: 0, y: 2 },
  B: { x: -4, y: -4 },
  C: { x: 3, y: -4 },
  k: 0.6,
  k2: 0.4,
};
const BC_COLOR = 'var(--color-primary-500)';
const MN_COLOR = 'var(--color-accent-500)';

type Handle = 'A' | 'B' | 'C' | 'M' | 'N';
const HANDLES: readonly Handle[] = ['M', 'N', 'A', 'B', 'C'];

function inside(p: Pt): boolean {
  return p.x >= VP.xMin && p.x <= VP.xMax && p.y >= VP.yMin && p.y <= VP.yMax;
}

function valid(f: ThalesFigure): boolean {
  return (
    isTriangle(f) &&
    [pointAt(f.A, f.B, f.k), pointAt(f.A, f.C, f.k), pointAt(f.A, f.C, f.k2)].every(inside)
  );
}

export function ThalesTool(_: ToolProps) {
  const [fig, setFig] = useState<ThalesFigure>(START);
  const [linked, setLinked] = useState(true);
  const [goal, setGoal] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const r = read(fig, linked);

  const update = (next: ThalesFigure) => setFig((old) => (valid(next) ? next : old));

  const drag = useDrag<Handle>({
    svgRef,
    frame,
    ids: HANDLES,
    pointOf: (h) => (h === 'M' ? r.M : h === 'N' ? r.N : fig[h]),
    onDrag: (h, p) => {
      if (h === 'M') update({ ...fig, k: snapK(projectParam(fig.A, fig.B, p)) });
      else if (h === 'N') {
        const k = snapK(projectParam(fig.A, fig.C, p));
        update(linked ? { ...fig, k } : { ...fig, k2: k });
      } else update({ ...fig, [h]: snapPoint(p, VP, 1, 0.5) });
    },
  });

  const chooseGoal = (i: number | null) => {
    setGoal(i);
    if (i !== null) setLinked(GOALS[i]!.linked);
  };
  const goalDone = goal !== null && GOALS[goal]!.linked === linked && GOALS[goal]!.check(fig);

  const px = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
  const far = (p: Pt, q: Pt) => {
    const a = pointAt(p, q, -20);
    const b = pointAt(p, q, 20);
    return { x1: frame.tx(a.x), y1: frame.ty(a.y), x2: frame.tx(b.x), y2: frame.ty(b.y) };
  };
  const [pa, pb, pc, pm, pn] = [fig.A, fig.B, fig.C, r.M, r.N].map(px) as [
    { x: number; y: number },
    { x: number; y: number },
    { x: number; y: number },
    { x: number; y: number },
    { x: number; y: number },
  ];

  const equalAB_AC = Math.abs(r.rAB - r.rAC) < 1e-9;
  const verdict: { tone: 'success' | 'error'; text: string } = linked
    ? {
        tone: 'success',
        text: `(MN) est parallèle à (BC) : les trois rapports sont égaux (théorème de Thalès).${
          r.butterfly ? ' Configuration papillon : M et N sont de l’autre côté de A.' : ''
        }`,
      }
    : equalAB_AC && r.sameOrder
      ? {
          tone: 'success',
          text: 'AM/AB = AN/AC et les points A, M, B et A, N, C sont dans le même ordre : (MN) est parallèle à (BC) (réciproque).',
        }
      : equalAB_AC
        ? {
            tone: 'error',
            text: 'Rapports égaux, mais les points ne sont pas dans le même ordre : (MN) n’est pas parallèle à (BC).',
          }
        : {
            tone: 'error',
            text: 'AM/AB ≠ AN/AC : (MN) n’est pas parallèle à (BC) (contraposée de Thalès).',
          };

  const rows = [
    { tex: '\\dfrac{AM}{AB}', v: r.rAB, a: r.am, b: r.ab, color: INK },
    { tex: '\\dfrac{AN}{AC}', v: r.rAC, a: r.an, b: r.ac, color: INK },
    { tex: '\\dfrac{MN}{BC}', v: r.rBC, a: r.mn, b: r.bc, color: MN_COLOR },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Mode"
          value={linked ? 'thales' : 'reciproque'}
          options={[
            { value: 'thales', label: 'Thalès' },
            { value: 'reciproque', label: 'Réciproque' },
          ]}
          onChange={(v) => setLinked(v === 'thales')}
        />
        <ToggleChip on={goal !== null} onClick={() => chooseGoal(goal === null ? 0 : null)}>
          Défi
        </ToggleChip>
      </div>

      {goal !== null && (
        <section
          aria-live="polite"
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          data-testid="thales-goal"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {goal + 1}/{GOALS.length} · {GOALS[goal]!.text}
          </p>
          {goalDone ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">Réussi !</p>
              {goal + 1 < GOALS.length && (
                <button
                  type="button"
                  onClick={() => chooseGoal(goal + 1)}
                  className={cn(PRIMARY_BUTTON, 'ml-auto')}
                >
                  Défi suivant
                </button>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">
              Fais glisser {GOALS[goal]!.linked ? 'M ou N' : 'N'} sur sa droite.
            </p>
          )}
        </section>
      )}

      <div className="mx-auto w-full max-w-[24rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label="Deux droites sécantes en A, le segment [BC] et le segment [MN]"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} labels={false} axes={false} />
          <line {...far(fig.A, fig.B)} stroke={MUTED} strokeWidth={1.25} />
          <line {...far(fig.A, fig.C)} stroke={MUTED} strokeWidth={1.25} />
          <polygon
            points={`${pa.x},${pa.y} ${pb.x},${pb.y} ${pc.x},${pc.y}`}
            fill="var(--color-primary-500)"
            fillOpacity={0.07}
            stroke="none"
          />
          <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={INK} strokeWidth={2} />
          <line x1={pa.x} y1={pa.y} x2={pc.x} y2={pc.y} stroke={INK} strokeWidth={2} />
          <line
            x1={pb.x}
            y1={pb.y}
            x2={pc.x}
            y2={pc.y}
            stroke={BC_COLOR}
            strokeWidth={3.5}
            strokeLinecap="round"
          />
          <line
            x1={pm.x}
            y1={pm.y}
            x2={pn.x}
            y2={pn.y}
            stroke={MN_COLOR}
            strokeWidth={3.5}
            strokeLinecap="round"
            strokeDasharray={r.parallel ? undefined : '7 5'}
          />
          {(['A', 'B', 'C'] as const).map((h) => {
            const p = px(fig[h]);
            const g = { x: (pa.x + pb.x + pc.x) / 3, y: (pa.y + pb.y + pc.y) / 3 };
            const d = Math.hypot(p.x - g.x, p.y - g.y) || 1;
            return (
              <DragHandle
                key={h}
                x={p.x}
                y={p.y}
                color={INK}
                label={h}
                labelDx={((p.x - g.x) / d) * 20}
                labelDy={((p.y - g.y) / d) * 20}
                name={`Point ${h} (${fig[h].x} ; ${fig[h].y})`}
                active={drag.active === h}
                keyProps={drag.keyProps(h)}
              />
            );
          })}
          <DragHandle
            x={pm.x}
            y={pm.y}
            color={MN_COLOR}
            label="M"
            labelDx={-18}
            labelDy={0}
            name={`Point M, sur la droite (AB), AM/AB = ${formatNumber(r.rAB, 2)}`}
            active={drag.active === 'M'}
            keyProps={drag.keyProps('M')}
          />
          <DragHandle
            x={pn.x}
            y={pn.y}
            color={MN_COLOR}
            label="N"
            labelDx={18}
            labelDy={0}
            name={`Point N, sur la droite (AC), AN/AC = ${formatNumber(r.rAC, 2)}`}
            active={drag.active === 'N'}
            keyProps={drag.keyProps('N')}
          />
        </svg>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ParamSlider
          name="Position de M sur (AB)"
          ariaLabel="Position de M : AM/AB, négatif de l’autre côté de A"
          value={fig.k}
          min={K_MIN}
          max={K_MAX}
          step={K_STEP}
          format={(v) => `k = ${formatNumber(v, 2)}`}
          onChange={(v) => update({ ...fig, k: snapK(v, fig.k) })}
          color={MN_COLOR}
        />
        {!linked && (
          <ParamSlider
            name="Position de N sur (AC)"
            ariaLabel="Position de N : AN/AC, négatif de l’autre côté de A"
            value={fig.k2}
            min={K_MIN}
            max={K_MAX}
            step={K_STEP}
            format={(v) => `k′ = ${formatNumber(v, 2)}`}
            onChange={(v) => update({ ...fig, k2: snapK(v, fig.k2) })}
            color={MN_COLOR}
          />
        )}
      </div>

      <section aria-label="Rapports" className="grid grid-cols-3 gap-2" data-testid="thales-ratios">
        {rows.map((row, i) => {
          const off = !linked && i === 1 && !equalAB_AC;
          return (
            <div
              key={row.tex}
              className={cn(
                'rounded-2xl border px-2 py-2 text-center',
                off ? 'border-error-500' : 'border-ink-100',
              )}
            >
              <MathText className="text-ink-900 block text-base">{`$${row.tex}$`}</MathText>
              <span className="font-display text-ink-900 mt-1 block text-lg font-bold tabular-nums">
                {formatNumber(row.v, 2)}
              </span>
              <span className="text-ink-500 block text-xs">
                ≈ {formatNumber(row.a, 1)} ÷ {formatNumber(row.b, 1)}
              </span>
            </div>
          );
        })}
      </section>

      <Note tone={verdict.tone}>{verdict.text}</Note>
    </div>
  );
}
